// Abre a última conversa acessível do contato (aberta recente, senão a mais recente).
// Distingue: sem conversa / sem permissão / sucesso — para o clique no nome não
// "sumir" caindo silencioso nos detalhes.
// Sem conversa → modal Nova conversa com o contato (BUS_EVENTS.NEW_CONVERSATION_MODAL).
// Resolvida → reabre (toggle_status open) antes de navegar, para não sumir do filtro "abertas".

import ContactAPI from 'dashboard/api/contacts';
import ConversationAPI from 'dashboard/api/inbox/conversation';
import {
  isConversationOpen,
  resolveConversationToOpen,
} from 'dashboard/components-next/NewConversation/helpers/conversationReuseHelper';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { useAlert } from 'dashboard/composables';
import { CONVERSATION_STATUS } from 'shared/constants/messages';

/**
 * @returns {{ kind: 'opened', conversationId: number, status?: string|number }
 *   | { kind: 'no_conversations' }
 *   | { kind: 'no_access' }
 *   | { kind: 'error', error: unknown }}
 */
export async function openLatestAccessibleConversation(contactId) {
  try {
    const { data } = await ContactAPI.getConversations(contactId);
    const payload = data?.payload || [];
    const meta = data?.meta || {};
    const total = Number(meta.total_conversations ?? payload.length);
    const accessible = Number(meta.accessible_conversations ?? payload.length);
    const conversation = resolveConversationToOpen(payload);

    if (conversation?.id) {
      return {
        kind: 'opened',
        conversationId: conversation.id,
        status: conversation.status,
      };
    }

    if (total > 0 && accessible === 0) {
      return { kind: 'no_access' };
    }

    return { kind: 'no_conversations' };
  } catch (error) {
    return { kind: 'error', error };
  }
}

/** Abre o compose global com o contato pré-selecionado (conversa “vazia”). */
export function emitComposeForContact(contactId) {
  emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, {
    contactId: Number(contactId),
  });
}

/**
 * Clique no nome: última conversa (reabre se resolvida), senão compose; toasts nos outros casos.
 * @returns {Promise<'opened'|'compose'|'no_access'|'error'>}
 */
export async function openContactChatOrCompose({
  contactId,
  accountId,
  router,
  t,
}) {
  const result = await openLatestAccessibleConversation(contactId);

  if (result.kind === 'opened') {
    // Resolvida fora do filtro "abertas" → reabrir antes do navigate (evita spinner eterno).
    if (!isConversationOpen({ status: result.status })) {
      try {
        await ConversationAPI.toggleStatus({
          conversationId: result.conversationId,
          status: CONVERSATION_STATUS.OPEN,
        });
      } catch (error) {
        useAlert(t('CONTACTS_LAYOUT.CARD.OPEN_CONVERSATION_ERROR'));
        return 'error';
      }
    }

    router.push(
      `/app/accounts/${accountId}/conversations/${result.conversationId}`
    );
    return 'opened';
  }

  if (result.kind === 'no_access') {
    useAlert(t('CONTACTS_LAYOUT.CARD.NO_ACCESSIBLE_CONVERSATION'));
    return 'no_access';
  }

  if (result.kind === 'no_conversations') {
    emitComposeForContact(contactId);
    return 'compose';
  }

  useAlert(t('CONTACTS_LAYOUT.CARD.OPEN_CONVERSATION_ERROR'));
  return 'error';
}
